import { toast as sonner } from "sonner";

/**
 * First-party toast wrapper over sonner so the app never imports sonner directly — tone is
 * consistent and the backing library is swappable in one place.
 *
 *   const toast = useToast();
 *   toast.success("Saved");
 *   toast.error("Couldn't save");
 *   toast.undo("Deleted", () => restore());        // action toast with an Undo
 *   toast.promise(api.post(...), { loading, success, error });
 */
const DEFAULTS = {
  actionLabel: "Undo",
  dismissLabel: "Dismiss",
  undoErrorMessage: "Couldn't undo — please try again.",
  promiseLoading: "Working…",
  promiseSuccess: "Done",
  promiseError: "Something went wrong — please try again.",
};

const ERROR_DURATION = 8000;

const displayMessage = (value) =>
  value && typeof value === "object" && typeof value.message === "string"
    ? value.message
    : value;

const toMessage = (err, fallback) =>
  err?.userMessage || err?.response?.data?.detail?.message || err?.response?.data?.message || err?.message || fallback;

export const notify = {
  success: (msg, opts) => sonner.success(msg, opts),
  // Errors linger and announce assertively so they aren't missed (WCAG 4.1.3); callers can override.
  error: (msg, opts = {}) =>
    sonner.error(displayMessage(msg), { duration: ERROR_DURATION, important: true, ...opts }),
  // Persistent variant for critical failures that must not auto-dismiss.
  errorPersistent: (msg, opts = {}) =>
    sonner.error(displayMessage(msg), { duration: Infinity, important: true, ...opts }),
  warning: (msg, opts) => sonner.warning(msg, opts),
  // Distinct info affordance (icon/role) instead of a plain neutral message.
  info: (msg, opts) => sonner.info(msg, opts),
  message: (msg, opts) => sonner(msg, opts),
  loading: (msg, opts) => sonner.loading(msg, opts),
  promise: (p, msgs = {}) => {
    const {
      loading = DEFAULTS.promiseLoading,
      success = DEFAULTS.promiseSuccess,
      error = (err) => toMessage(err, DEFAULTS.promiseError),
      ...rest
    } = msgs;
    return sonner.promise(p, { loading, success, error, ...rest });
  },
  dismiss: (id) => sonner.dismiss(id),
  /**
   * Success toast with a one-tap Undo (the workhorse for optimistic delete/change).
   * It persists until the user acts or dismisses it (WCAG 2.2.1 — never race a recovery
   * action), awaits the async restore, and surfaces an assertive error if the restore fails
   * so data loss is never silent. Pass actionLabel/dismissLabel/undoErrorMessage to localise.
   */
  undo: (msg, onUndo, opts = {}) => {
    const {
      actionLabel = DEFAULTS.actionLabel,
      dismissLabel = DEFAULTS.dismissLabel,
      undoErrorMessage = DEFAULTS.undoErrorMessage,
      duration,
      ...toastOpts
    } = opts;
    let id;
    id = sonner.success(msg, {
      ...toastOpts,
      duration: duration ?? Infinity,
      action: {
        label: actionLabel,
        onClick: async (event) => {
          event?.preventDefault?.();
          try {
            await onUndo?.();
            sonner.dismiss(id);
          } catch (err) {
            sonner.error(toMessage(err, undoErrorMessage), {
              id: `${id}-undo-error`,
              duration: ERROR_DURATION,
              important: true,
            });
          }
        },
      },
      cancel: { label: dismissLabel },
    });
    return id;
  },
};

export function useToast() {
  return notify;
}
